/**
 * SFrame180 – Notify Interested Parties: Phase Overview
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { PhaseOverview, type PhaseStepState } from '../settlement';
import NotificationTrackingPanel from '../settlement/NotificationTrackingPanel';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import type { SettlementStep } from '../../utils/settlementFlowConfig';
import { SETTLEMENT_PHASE_CONFIGS } from '../../utils/settlementFlowConfig';
import { SETTLEMENT_PLANNER_LAYOUT } from '../settlement/settlementTheme';

const config = SETTLEMENT_PHASE_CONFIGS['notify'];

const stepRoutes: Record<string, string> = {
  'notify-overview-1': '/sframe-181',
  'notify-overview-2': '/sframe-183',
  'notify-overview-3': '/sframe-186',
};

const consolidatedTileConfig: Array<{
  id: string;
  stepNumber: number;
  title: string;
  description: string;
  sourceStepIds: string[];
}> = [
  {
    id: 'notify-overview-1',
    stepNumber: 1,
    title: 'Official Notifications',
    description: 'Update government records and start the creditor claims process.',
    sourceStepIds: ['not-1'],
  },
  {
    id: 'notify-overview-2',
    stepNumber: 2,
    title: 'Financial & Insurance Notifications',
    description: 'Collect insurance proceeds and settle employment benefits.',
    sourceStepIds: ['not-2'],
  },
  {
    id: 'notify-overview-3',
    stepNumber: 3,
    title: 'Accounts & Services',
    description: 'Cancel unnecessary services and close or memorialize accounts.',
    sourceStepIds: ['not-3'],
  },
];

function consolidateStatus(statuses: Array<PhaseStepState['status']>): PhaseStepState['status'] {
  if (statuses.length > 0 && statuses.every((status) => status === 'complete')) {
    return 'complete';
  }
  if (statuses.some((status) => status === 'in-progress' || status === 'complete')) {
    return 'in-progress';
  }
  return 'not-started';
}

function buildConsolidatedSteps(progressData: Awaited<ReturnType<ReturnType<typeof useSettlementSession>['loadPhaseProgress']>>): PhaseStepState[] {
  return consolidatedTileConfig.map((tile) => {
    const sourceSteps = tile.sourceStepIds
      .map((id) => config.steps.find((step) => step.id === id))
      .filter((step): step is SettlementStep => Boolean(step));
    const sourceProgress = tile.sourceStepIds
      .map((id) => progressData.find((row) => row.stepId === id));
    const statuses = sourceProgress
      .map((row) => (row?.status as PhaseStepState['status']) || 'not-started');

    const total = sourceSteps.reduce(
      (sum, step) => sum + (step.subTasks?.reduce((subSum, subTask) => subSum + (subTask.checklistItems?.length || 0), 0) || 0),
      0
    );
    const completed = sourceProgress.reduce((sum, row) => sum + (row?.completedChecklistItems?.length || 0), 0);

    return {
      step: {
        id: tile.id,
        stepNumber: tile.stepNumber,
        title: tile.title,
        description: tile.description,
        category: 'Notifications',
        estimatedMinutes: sourceSteps.reduce((sum, step) => sum + (step.estimatedMinutes || 0), 0),
        frames: [stepRoutes[tile.id]],
      },
      status: consolidateStatus(statuses),
      subTaskProgress: { completed, total },
    };
  });
}

export default function SFrame180() {
  const { estateId } = useParams<{ estateId: string }>();
  const navigate = useNavigate();

  // Hooks must all be called before any early return (Rules of Hooks).
  // useSettlementSession handles null estateId gracefully (all writes are no-ops).
  const { loadPhaseProgress, error } = useSettlementSession(estateId ?? null);

  const [steps, setSteps] = useState<PhaseStepState[]>(
    buildConsolidatedSteps([])
  );

  const phaseChecklist = useMemo(() => {
    const total = steps.reduce((sum, step) => sum + (step.subTaskProgress?.total || 0), 0);
    const completed = steps.reduce((sum, step) => sum + (step.subTaskProgress?.completed || 0), 0);
    return { label: 'Checklist items', completed, total };
  }, [steps]);

  useEffect(() => {
    if (!estateId) return;
    const load = async () => {
      const progressData = await loadPhaseProgress('notify');
      setSteps(buildConsolidatedSteps(progressData));
    };
    load();
  }, [estateId, loadPhaseProgress]);

  // Guard: estateId is required for all save operations — redirect to estate
  // selector so the user re-enters via the proper route with an estateId.
  if (!estateId) return <Navigate to="/estate-selector" replace />;

  const handleStepClick = (stepId: string) => {
    const route = stepRoutes[stepId];
    if (route) navigate(estateId ? `${route}/${estateId}` : route);
  };

  return (
    <>
      {error && (
        <div className={`${SETTLEMENT_PLANNER_LAYOUT.container} mb-4`}>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
        </div>
      )}

      <PhaseOverview
        title="Notify Interested Parties"
        subtitle="Notify the right people once, in grouped steps with checklist-style guidance."
        activeSectionLabel="Notifications"
        layoutVariant="planner"
        steps={steps}
        estimatedMinutes={steps.filter((s) => s.status !== 'complete').reduce((sum, s) => sum + (s.step.estimatedMinutes || 0), 0)}
        onStepClick={handleStepClick}
        phaseChecklist={phaseChecklist}
        notificationTrackerContent={
          <NotificationTrackingPanel storageFrameId="sframe-180" preset="official" />
        }
      />
    </>
  );
}
